// vue
import { onUnmounted, reactive, ref, type Ref, watch } from 'vue'
// router
import { useRouter } from 'vue-router'
// libraries
import Swal from 'sweetalert2'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
// utilities
import { alertToast } from '@/utilities/notification'
// composables
import useApiRequest from '@/composables/useApiRequest'
import useMeilisearch from '@/composables/useMeilisearch'

/**
 * Composable for managing agreement merge operations
 * Handles MeiliSearch integration, validation, and merge submission
 */
export default function useAgreementMerge(agreementID: Ref<string | number>, dealershipIDs: Ref<number[]>) {
  const router = useRouter()

  // state
  const mergeModalIsOpen = ref(false)
  const searchTerm = ref('')
  const meiliSearchResults = ref<{ hits?: Array<Record<string, unknown>> }>({})
  const agreementsList = ref<Array<Record<string, unknown>>>([])
  const { createSearch } = useMeilisearch()

  const mergeAgreementData = reactive({
    from: {
      agreementID: parseInt(String(agreementID.value))
    },
    to: null as number | null
  })

  // api requests
  const mergeAgreement = useApiRequest()

  // validation
  const mergeRules = {
    from: {
      agreementID: { required }
    },
    to: {
      required,
      mergeToIsValid: helpers.withMessage('Cannot merge into itself', value => {
        return value != mergeAgreementData.from
      })
    }
  }

  const mergeV$ = useVuelidate(mergeRules, mergeAgreementData)

  /**
   * Searches agreements using MeiliSearch.
   */
  const { scheduleSearch, dispose: disposeSearch } = createSearch(
    async ({ client, signal, query }) => {
      return client
        .index('agreements')
        .search(
          query,
          {
            filter: 'dealershipId IN [' + dealershipIDs.value.join(',') + ']'
          },
          { signal }
        )
    },
    {
      debounceMs: 300,
      onClear: () => {
        meiliSearchResults.value = {}
        agreementsList.value = []
      },
      onResult: results => {
        meiliSearchResults.value = results
        if (meiliSearchResults.value.hits != undefined) {
          agreementsList.value = meiliSearchResults.value.hits
        }
      }
    }
  )

  watch(searchTerm, newSearchTerm => {
    scheduleSearch(newSearchTerm)
  })

  onUnmounted(() => {
    disposeSearch()
  })

  /**
   * Submits merge agreement request
   */
  async function submitMergeAgreement(): Promise<void> {
    // Manual validation since Vuelidate seems to have conflicts with reactive objects
    if (!mergeAgreementData.from?.agreementID) {
      alertToast('Validation failed', 'Source agreement ID is missing', 'error')
      return
    }

    if (!mergeAgreementData.to) {
      alertToast('Validation failed', 'Please select an agreement to merge into', 'error')
      return
    }

    if (mergeAgreementData.to === mergeAgreementData.from.agreementID) {
      alertToast('Validation failed', 'Cannot merge into itself', 'error')
      return
    }

    // Validate with Vuelidate for UI error display, but don't block on it
    await mergeV$.value.$validate()

    Swal.fire({
      title: 'Are you sure?',
      text: 'This cannot be reversed. Please triple check before merging.',
      icon: 'warning',
      reverseButtons: true,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Confirm Merge',
      confirmButtonColor: 'var(--bg-brand-solid)',
      cancelButtonText: 'Cancel',
      cancelButtonColor: 'var(--bg-secondary)'
    }).then(function (response) {
      if (response.isConfirmed) {
        const jsonData = JSON.stringify({
          to: mergeAgreementData.to
        })

        mergeAgreement
          .send({
            endpoint: '/v1/agreements/' + agreementID.value + '/merge',
            method: 'PATCH',
            data: jsonData
          })
          .then(response => {
            if (response.success) {
              alertToast(
                'Agreement Merged',
                'Agreement #' + agreementID.value + ' has been merged into #' + mergeAgreementData.to,
                'success'
              )
              router.replace('/agreement/' + mergeAgreementData.to)
            } else {
              alertToast('Failed to merge agreement #' + agreementID.value, response.message ?? undefined, 'error')
            }
            // Close modal
            mergeModalIsOpen.value = false
          })
      }
    })
  }

  return {
    // state
    mergeModalIsOpen,
    mergeAgreementData,
    searchTerm,
    agreementsList,
    mergeV$,
    mergeAgreement,
    // methods
    submitMergeAgreement
  }
}
